import { HistoryTread } from "@/components/cases/case-history-thread";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/core/actions/dropdown-menu";
import Badge from "@/components/core/badge";
import { Blanket, BlanketImage, BlanketInfo } from "@/components/core/blanket.tsx";
import { Button } from "@/components/core/buttons/button";
import { FileLinkWithViewer } from "@/components/core/file-link-with-viewer";
import Tooltip from "@/components/core/tooltip";
import { caseStages } from "@/constants/case-stages";
import { formatDate } from "@/utils/format-date";
import { IconCancel, IconChevronDown, IconCircleDot, IconDatabaseExclamation, IconFilter, IconGavel } from "@tabler/icons-react";
import { useMemo, useState } from "react";

const CaseComplaintThread = ({ data }) => {
	// Separate regular complaints from amended complaints
	const regularComplaints = data.medias?.filter((media) => !media.custom_properties?.is_amend) || [];
	const amendedComplaints = data.medias?.filter((media) => media.custom_properties?.is_amend === true) || [];

	return (
		<div className="relative flex flex-col w-full gap-2 mb-6">
			<div className="absolute right-full -top-1 mr-2 inline-flex w-[32px] h-[32px] rounded-full justify-center items-center bg-background text-primary-600">
				<IconCircleDot size={18} stroke={2} />
			</div>
			<div className="flex flex-row items-center w-full gap-2">
				<span className="text-base font-bold text-foreground-dark">Case Filing</span>
			</div>
			{regularComplaints.length > 0 && (
				<div className="flex flex-col w-full rounded-md border border-border/50">
					<div className="flex flex-col w-full p-4">
						<div className="flex flex-row items-center gap-2">
							<div className="inline-flex shrink-0 self-start justify-center items-center w-[32px] h-[32px] rounded-full text-primary-foreground bg-primary-500">
								<IconGavel size={18} stroke={2} />
							</div>
							<div className="flex flex-col w-full">
								<div className="inline-flex flex-row items-center justify-between gap-1">
									<span className="font-semibold text-base text-foreground-dark">Case Complaint</span>
									<span className="text-base text-foreground/70">Filed on {data.filed_at ? formatDate(data.filed_at, "PPp") : "N/A"}</span>
								</div>
								<div className="flex flex-row flex-wrap gap-1.5 mt-2">
									{regularComplaints.map((file, index) => (
										<FileLinkWithViewer key={index} file={file} url={file.original_url} maxLength={20} mime_type={file.mime_type || ""} />
									))}
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
			{amendedComplaints.length > 0 && (
				<div className="flex flex-col w-full rounded-md border border-border/50">
					<div className="flex flex-col w-full p-4">
						<div className="flex flex-row items-center gap-2">
							<div className="inline-flex shrink-0 self-start justify-center items-center w-[32px] h-[32px] rounded-full text-primary-foreground bg-amber-500">
								<IconGavel size={18} stroke={2} />
							</div>
							<div className="flex flex-col w-full">
								<div className="inline-flex flex-row items-center justify-between gap-1">
									<span className="font-semibold text-base text-foreground-dark line-through">Amended Complaint</span>
									<span className="text-base text-foreground/70">Filed on {data.filed_at ? formatDate(data.filed_at, "PPp") : "N/A"}</span>
								</div>
								<div className="flex flex-row flex-wrap gap-1.5 mt-2">
									{amendedComplaints.map((file, index) => (
										<Tooltip
											key={index}
											content={
												file.custom_properties?.amended_at
													? `Amended on: ${formatDate(file.custom_properties.amended_at, "PPp")}`
													: "Amendment date not available"
											}
										>
											<div className="inline-flex">
												<FileLinkWithViewer file={file} url={file.original_url} maxLength={20} mime_type={file.mime_type || ""} />
											</div>
										</Tooltip>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
			{data.annexes &&
				data.annexes.length > 0 &&
				(() => {
					// Separate regular annexes from amended annexes
					const regularAnnexes = data.annexes.filter((annex) => !annex.medias?.[0]?.custom_properties?.is_amend);
					const amendedAnnexes = data.annexes.filter((annex) => annex.medias?.[0]?.custom_properties?.is_amend === true);

					return (
						<>
							{regularAnnexes.length > 0 && (
								<div className="flex flex-col w-full rounded-md border border-border/50">
									<div className="flex flex-col w-full p-4">
										<div className="flex flex-row items-center gap-2">
											<div className="inline-flex shrink-0 self-start justify-center items-center w-[32px] h-[32px] rounded-full text-primary-foreground bg-blue-500">
												<IconGavel size={18} stroke={2} />
											</div>
											<div className="flex flex-col w-full">
												<div className="inline-flex flex-row items-center justify-between gap-1">
													<span className="font-semibold text-base text-foreground-dark">Annexes</span>
													<span className="text-base text-foreground/70">Filed on {data.filed_at ? formatDate(data.filed_at, "PPp") : "N/A"}</span>
												</div>
												<div className="flex flex-row flex-wrap gap-1.5 mt-2">
													{regularAnnexes.map((file, index) => (
														<FileLinkWithViewer
															key={index}
															file={file.medias[0]}
															url={file.medias[0].original_url}
															maxLength={20}
															mime_type={file.medias[0].mime_type || ""}
														/>
													))}
												</div>
											</div>
										</div>
									</div>
								</div>
							)}
							{amendedAnnexes.length > 0 && (
								<div className="flex flex-col w-full rounded-md border border-border/50">
									<div className="flex flex-col w-full p-4">
										<div className="flex flex-row items-center gap-2">
											<div className="inline-flex shrink-0 self-start justify-center items-center w-[32px] h-[32px] rounded-full text-primary-foreground bg-amber-500">
												<IconGavel size={18} stroke={2} />
											</div>
											<div className="flex flex-col w-full">
												<div className="inline-flex flex-row items-center justify-between gap-1">
													<span className="font-semibold text-base text-foreground-dark line-through">Amended Annexes</span>
													<span className="text-base text-foreground/70">Filed on {data.filed_at ? formatDate(data.filed_at, "PPp") : "N/A"}</span>
												</div>
												<div className="flex flex-row flex-wrap gap-1.5 mt-2">
													{amendedAnnexes.map((file, index) => (
														<Tooltip
															key={index}
															content={
																file.medias[0].custom_properties?.amended_at
																	? `Amended on: ${formatDate(file.medias[0].custom_properties.amended_at, "PPp")}`
																	: "Amendment date not available"
															}
														>
															<div className="inline-flex">
																<FileLinkWithViewer
																	file={file.medias[0]}
																	url={file.medias[0].original_url}
																	maxLength={20}
																	mime_type={file.medias[0].mime_type || ""}
																/>
															</div>
														</Tooltip>
													))}
												</div>
											</div>
										</div>
									</div>
								</div>
							)}
						</>
					);
				})()}
		</div>
	);
};

export const CaseHistory = ({ groups, activities, data }) => {
	const hasData = true;
	const ongoing = true;
	const [selectedAuthor, setSelectedAuthor] = useState<string>("all");

	type Activity = {
		author?: {
			full_name?: string;
		};
		party_type?: string;
		[key: string]: any;
	};

	const uniqueAuthors = useMemo(() => {
		const authors = new Set<string>();
		if (activities) {
			for (const activity of activities as Activity[]) {
				if (activity?.author?.full_name) {
					authors.add(`${activity.author.full_name} (${activity.party_type})`);
				}
			}
		}
		return Array.from(authors).sort();
	}, [activities]);

	const filteredGroups = useMemo(() => {
		if (selectedAuthor === "all") return groups;

		return groups
			.map(([stage, activityGroup]) => [
				stage,
				activityGroup.filter((activity) => selectedAuthor === "all" || `${activity?.author?.full_name} (${activity.party_type})` === selectedAuthor),
			])
			.filter(([stage, activityGroup]) => activityGroup.length > 0);
	}, [groups, selectedAuthor]);

	const getDays = (stage: string) => {
		const previousStageData = data.stages.find((s) => s.previous_stage === stage);
		const currentStage = data.stages.find((s) => s.stage === stage);

		if (previousStageData) return `${currentStage.days_in_stage} day/s`;

		if (stage === "case_closed" || stage === "with_appeal") {
			return `${currentStage?.days_in_stage} day/s`;
		}

		if (stage === caseStages.PRELIMINARY_PROCEEDINGS) {
			const daysElapsed = Math.ceil((new Date() - new Date(data.filed_at)) / (1000 * 60 * 60 * 24));
			return `${daysElapsed} day/s`;
		}

		if (currentStage?.created_at) {
			const daysElapsed = Math.ceil((new Date() - new Date(currentStage.created_at)) / (1000 * 60 * 60 * 24));
			return `${daysElapsed} day/s`;
		}

		return null;
	};

	return (
		<div className="flex flex-col w-full mt-6">
			{hasData ? (
				<div className="flex flex-col w-full pl-3">
					<div className="flex flex-col w-full pl-6 gap-6 border-l border-border/50">
						{/* Author Filter Dropdown */}
						<div className="flex flex-row items-center justify-between w-full mb-4">
							<span className="text-lg font-semibold text-foreground-dark" />
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button variant="ghost" size="sm" className="gap-2">
										<IconFilter size={16} stroke={1.5} />
										<span className="text-sm">{selectedAuthor === "all" ? "All Authors" : selectedAuthor}</span>
										<IconChevronDown size={14} stroke={1.5} />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									<DropdownMenuItem onClick={() => setSelectedAuthor("all")} className={selectedAuthor === "all" ? "bg-accent" : ""}>
										All Authors
									</DropdownMenuItem>
									{uniqueAuthors.map((author) => (
										<DropdownMenuItem key={author} onClick={() => setSelectedAuthor(author)} className={selectedAuthor === author ? "bg-accent" : ""}>
											{author}
										</DropdownMenuItem>
									))}
								</DropdownMenuContent>
							</DropdownMenu>
						</div>
						<CaseComplaintThread data={data} />
						{filteredGroups.map(([stage, activityGroup], index) => (
							<div key={index} className="relative flex flex-col w-full gap-2">
								<div className="absolute right-full -top-1 mr-2 inline-flex w-[32px] h-[32px] rounded-full justify-center items-center bg-background text-primary-600">
									<IconCircleDot size={18} stroke={2} />
								</div>
								<div className="flex flex-row items-center w-full gap-2">
									<span className="text-base font-bold text-foreground-dark">{stage}</span>
									{getDays(stage) !== null && (
										<Badge className="uppercase" size="xs" color="gray-light">
											{getDays(stage)}
										</Badge>
									)}
								</div>
								{activityGroup?.map((data, catIndex) => (
									<HistoryTread key={catIndex} activity={data} />
								))}
								{/*{ongoing && (
                  <div className="flex flex-col w-full rounded-md border border-border/50">
                    <Blanket width="sm">
                      <BlanketImage
                        icon={<IconGavel size={24} stroke={1.5} />}
                      />
                      <BlanketInfo
                        title="Ongoing"
                        message="You will see the activities and other after moving to the next process"
                      />
                    </Blanket>
                  </div>
                )}*/}
							</div>
						))}

						{(data.status === "case_closed" || data.status === "with_appeal") && (
							<div className="relative flex flex-col w-full gap-2">
								<div className="absolute right-full -top-1 mr-2 inline-flex w-[32px] h-[32px] rounded-full justify-center items-center bg-background text-primary-600">
									<IconCancel size={18} stroke={2} />
								</div>
								<div className="flex flex-row items-center w-full gap-2">
									<span className="text-base font-bold text-danger-600">{data.status === "case_closed" ? "Case Closed" : "With Appeal"}</span>
									<Badge className="uppercase" size="xs" color="red-light">
										{getDays(data.status)}
									</Badge>
								</div>
							</div>
						)}
					</div>
				</div>
			) : (
				<div className="flex flex-col w-full rounded-md border border-border/50">
					<Blanket width="sm">
						<BlanketImage icon={<IconDatabaseExclamation size={24} stroke={1.5} />} />
						<BlanketInfo title="No Data" message="No available data for now. Please check back later or refresh the page." />
					</Blanket>
				</div>
			)}
		</div>
	);
};
